/**
 * TPA Stream frontend analytics events → backend pipeline (Firehose → S3).
 * Fire-and-forget: failures must not interrupt the TPA user flow.
 */

import { getRequiredApiBaseUrl } from 'config/apiBaseUrl';

import { getIdToken, getUserIdToken } from 'services/auth';

import type {
  TpaStreamAnalyticsEventType,
  TpaStreamAnalyticsRequestBody,
  TpaStreamAnalyticsResponseBody,
} from 'types/tpaStreamAnalytics';

function resolveIdToken(): string | null {
  return getUserIdToken() ?? getIdToken();
}

function buildAnalyticsRequestBody(
  eventType: TpaStreamAnalyticsEventType
): TpaStreamAnalyticsRequestBody {
  return {
    eventType,
    timestamp: new Date().toISOString(),
  };
}

function isAnalyticsSuccessBody(body: unknown): body is TpaStreamAnalyticsResponseBody {
  return (
    body !== null &&
    typeof body === 'object' &&
    'success' in body &&
    (body as TpaStreamAnalyticsResponseBody).success === true
  );
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.trim() === '') {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * POST /tpa-stream/analytics with Cognito id token when available.
 * Never throws — callers treat analytics as best-effort.
 */
export async function postTpaStreamAnalyticsEvent(
  eventType: TpaStreamAnalyticsEventType
): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  let baseUrl: string;
  try {
    baseUrl = getRequiredApiBaseUrl();
  } catch {
    return;
  }

  const idToken = resolveIdToken();
  const body = JSON.stringify(buildAnalyticsRequestBody(eventType));
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
  }

  try {
    const response = await fetch(`${baseUrl}/tpa-stream/analytics`, {
      method: 'POST',
      headers,
      body,
      keepalive: eventType === 'account_connection_abandoned',
    });

    if (!response.ok) {
      return;
    }

    const responseBody = await readResponseBody(response);
    if (!isAnalyticsSuccessBody(responseBody)) {
      return;
    }
  } catch {
    // Analytics must not affect TPA UX.
  }
}
